// OPTIONS STRATEGY BUILDER · Box Spread. READ-ONLY analysis of ONE box (K1 < K2, LONG or SHORT) on any NFO
// index or MCX option chain: four legs priced live at their depth, every figure from the worker
// (box_spread_math), the theoretically FLAT expiry payoff with its minimum and maximum shown explicitly,
// estimated costs, broker basket margin from Zerodha's read-only calculator. With Box Spread SHADOW enabled
// (`trading`), "Create SHADOW trade" saves a PLANNED trade on the main backend - nothing is sent until a person
// presses Start on it, which re-validates everything on live data.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import api, { apiError } from "@/lib/api";
import { pct, rupees } from "@/components/options/PairValuation";
import { age, px } from "@/components/options/OptionsCommon";
import { CONTROL, FilterField } from "@/components/options/ScannerTable";
import LiveCreate from "@/components/iron_condor/LiveCreate";
import IcPayoffChart from "@/components/iron_condor/IcPayoffChart";
import { marginText, Tile } from "@/components/iron_condor/IcDetail";

export const BOX_LEGS = {
  LONG: [["BUY", "CE", 0], ["SELL", "PE", 0], ["SELL", "CE", 1], ["BUY", "PE", 1]],
  SHORT: [["SELL", "CE", 0], ["BUY", "PE", 0], ["BUY", "CE", 1], ["SELL", "PE", 1]],
};
const sideCls = (s) => (s === "BUY" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF3B30]");
const u2 = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));
const money = (r, u) => (r !== null && r !== undefined ? rupees(r) : u !== null && u !== undefined ? `${u2(u)} / unit` : "—");
const tone = (v) => (v === null || v === undefined ? undefined : v > 0 ? "good" : v < 0 ? "bad" : undefined);

/** Display mirror of box_spread_math.pnl_per_unit (leg by leg) - the worker is the source of truth. */
export function boxPnlUnit(direction, [k1, k2], prices, s) {
  return BOX_LEGS[direction].reduce((t, [side, kind, ki], i) => {
    const k = ki ? k2 : k1;
    const v = (kind === "CE" ? Math.max(s - k, 0) : Math.max(k - s, 0)) - prices[i];
    return t + (side === "BUY" ? v : -v);
  }, 0);
}

/** The order quantities one leg is sent in: `lots` total, at most `perOrderLots` per order. 7 x 65 at 2 ->
 * [130, 130, 130, 65]. */
export function slices(lots, lot, perOrderLots) {
  const out = [];
  let left = lots;
  const per = Math.max(1, perOrderLots || lots || 1);
  while (left > 0) { const n = Math.min(per, left); out.push(n * lot); left -= n; }
  return out;
}

/** "7 lots × 65 = 455 qty · 2 lots per order → 4 orders per leg (130 + 130 + 130 + 65)" */
export function sliceText(lots, lot, units, perOrderLots) {
  const qty = lots * lot;
  const u = units && units !== 1 ? ` (${(qty * units).toLocaleString("en-IN")} units)` : "";
  if (!perOrderLots) return `${lots} lot${lots > 1 ? "s" : ""} × ${lot} = ${qty} qty${u}`;
  const sl = slices(lots, lot, perOrderLots);
  return `${lots} lot${lots > 1 ? "s" : ""} × ${lot} = ${qty} qty${u} · ${perOrderLots} lot${perOrderLots > 1 ? "s" : ""} per order → `
    + `${sl.length} order${sl.length > 1 ? "s" : ""} per leg (${sl.join(" + ")}) · ${sl.length * 4} entry orders in all`;
}

export const validBox = (k) => k.length === 2 && k.every((x) => typeof x === "number" && Number.isFinite(x) && x > 0) && k[0] < k[1];

export default function BoxBuilder({ seed, running, trading = false, onCreated }) {
  const [unders, setUnders] = useState([]);
  const [cfg, setCfg] = useState({ exchange: "NFO", underlying: "", expiry: null, lots: 1, strikes: [null, null], direction: "LONG" });
  const [chain, setChain] = useState(null);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  const [margin, setMargin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shadow, setShadow] = useState(null);
  const [live, setLive] = useState(null);
  const [orderLimits, setOrderLimits] = useState(null);
  const [sizingMode, setSizingMode] = useState("FIXED_LOTS");
  const [alloc, setAlloc] = useState("");
  const [sized, setSized] = useState(null);
  const [lpo, setLpo] = useState(1);                       // lots per order (the user's choice)
  // a sizing is only valid for the box it was calculated for: any change to the box makes it stale
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSized(null); }, [cfg.exchange, cfg.underlying, cfg.expiry, cfg.direction, JSON.stringify(cfg.strikes)]);
  const [minNet, setMinNet] = useState("0");
  useEffect(() => {
    if (!trading) return;
    api.get("/box-spread/status").then((r) => { setShadow(r.data.shadow); setLive(r.data.live || null); setOrderLimits(r.data.order_limits || null); })
      .catch(() => { setShadow(null); setLive(null); });
  }, [trading]);
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  useEffect(() => { optionsApi.get("/ic/underlyings").then((r) => setUnders(r.data.underlyings || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (seed) setCfg({ exchange: seed.exchange, underlying: seed.underlying, expiry: seed.expiry, lots: seed.lots || 1,
      strikes: seed.strikes, direction: seed.direction || "LONG" });
  }, [seed]);
  useEffect(() => {
    if (!running || !cfg.underlying) { setChain(null); return; }
    optionsApi.get("/ic/builder/chain", { params: { exchange: cfg.exchange, underlying: cfg.underlying, ...(cfg.expiry ? { expiry: cfg.expiry } : {}) } })
      .then((r) => {
        setChain(r.data); setErr(null);
        const c = cfgRef.current;
        if (!c.expiry && r.data.expiry) setCfg((x) => ({ ...x, expiry: r.data.expiry }));
        if (c.strikes.some((k) => k == null) && r.data.spot) {
          const both = r.data.put_strikes.filter((k) => r.data.call_strikes.includes(k));
          const i = both.findIndex((k) => k >= r.data.spot);
          if (i > 0) setCfg((x) => ({ ...x, strikes: [both[i - 1], both[i]] }));
        }
      })
      .catch((e) => { setChain(null); setErr(optionsError(e)); });
  }, [running, cfg.exchange, cfg.underlying, cfg.expiry]);
  const strikes = useMemo(() => (chain ? chain.put_strikes.filter((k) => chain.call_strikes.includes(k)) : []), [chain]);
  const ready = running && cfg.underlying && validBox(cfg.strikes);
  const accountCap = orderLimits?.options_max_lots_per_order || 1;
  const effLpo = Math.min(lpo, accountCap);
  const allocModeB = sizingMode === "ALLOCATE_FUNDS";
  // the Builder analyses the RESOLVED position: Fixed lots -> Total lots; Allocate funds -> the calculated lots
  // (until calculated the figures are a 1-lot INDICATIVE view and nothing can be created)
  const resolvedLots = allocModeB ? (sized?.lots > 0 ? sized.lots : null) : cfg.lots;
  const body = useMemo(() => ({ exchange: cfg.exchange, underlying: cfg.underlying, expiry: cfg.expiry, lots: resolvedLots || 1,
    lots_per_order: effLpo, strikes: cfg.strikes, direction: cfg.direction }), [cfg, resolvedLots, effLpo]);
  useEffect(() => {
    setMargin(null);
    if (!ready) { setQuote(null); return undefined; }
    let alive = true;
    const load = () => optionsApi.post("/ic/box/builder/quote", body)
      .then((r) => { if (alive) { setQuote(r.data); setErr(null); } })
      .catch((e) => { if (alive) { setQuote(null); setErr(optionsError(e)); } });
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [ready, body]);
  const fetchMargin = async () => {
    setBusy(true);
    try { const r = await optionsApi.post("/ic/box/builder/margin", body); setMargin(r.data); }
    catch (e) { toast.error(optionsError(e)); }
    finally { setBusy(false); }
  };
  const legBody = (mode = "SHADOW") => ({ mode, direction: cfg.direction, exchange: quote.exchange, underlying: quote.underlying,
    expiry: quote.expiry, strikes: cfg.strikes, tradingsymbols: quote.legs.map((x) => x.tradingsymbol),
    plan_prices: quote.legs.map((x) => x.price), plan_limits: quote.legs.map((x) => x.limit) });
  const sizeIt = async () => {
    if (!quote?.metrics) return;
    setBusy(true);
    try { const r = await api.post("/box-spread/size", { ...legBody(), allocated_funds: Number(alloc) || 0, lots_per_order: lpo }); setSized(r.data); }
    catch (e) { setSized(null); toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const allocMode = sizingMode === "ALLOCATE_FUNDS";
  const createTrade = async (mode = "SHADOW") => {
    if (!quote?.metrics) return;
    setBusy(true);
    try {
      await api.post("/box-spread/trades", { ...legBody(mode), lots: allocMode ? (sized?.lots || 1) : cfg.lots,
        sizing_mode: sizingMode, lots_per_order: lpo, ...(allocMode ? { allocated_funds: Number(alloc) || 0 } : {}),
        min_net_pnl: Number(minNet) || 0, quoted_at: quote.generated_at });
      toast.success(`PLANNED ${mode} box created - nothing is sent until you press Start`);
      if (onCreated) onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const names = unders.filter((u) => u.exchange === cfg.exchange);
  const expiries = names.find((u) => u.underlying === cfg.underlying)?.expiries || [];
  const m = quote?.metrics;
  const cap = margin || quote?.capital;
  const roc = m && cap?.capital_required && (cap.status === "OK" || cap.status === "STALE") && m.net_pnl != null
    ? (m.net_pnl / cap.capital_required) * 100 : null;
  const [k1, k2] = cfg.strikes;
  const markers = validBox(cfg.strikes) ? [{ x: k1, label: `K1 ${k1}`, color: "#2563EB" }, { x: k2, label: `K2 ${k2}`, color: "#2563EB" }] : null;

  if (!running) return <div className="term-panel rounded-sm p-4 text-xs" data-testid="box-builder-off">Start the scanner to stream the Builder's legs.</div>;
  return (
    <div className="grid lg:grid-cols-[minmax(22rem,30rem)_1fr] gap-3" data-testid="box-builder">
      <div className="term-panel rounded-sm p-3 space-y-2 text-xs">
        <div className="flex flex-wrap gap-2 items-end">
          <FilterField label="Exchange"><select className={CONTROL} value={cfg.exchange}
            onChange={(e) => setCfg({ ...cfg, exchange: e.target.value, underlying: "", expiry: null, strikes: [null, null] })}>
            <option>NFO</option><option>MCX</option></select></FilterField>
          <FilterField label="Underlying"><input list="box-underlyings" className={`${CONTROL} w-36`} value={cfg.underlying}
            onChange={(e) => setCfg({ ...cfg, underlying: e.target.value.toUpperCase(), expiry: null, strikes: [null, null] })} data-testid="box-underlying" />
            <datalist id="box-underlyings">{names.map((u) => <option key={u.underlying} value={u.underlying} />)}</datalist></FilterField>
          <FilterField label="Expiry"><select className={CONTROL} value={cfg.expiry || ""} onChange={(e) => setCfg({ ...cfg, expiry: e.target.value || null, strikes: [null, null] })}>
            {!cfg.expiry && <option value="">nearest</option>}{expiries.map((x) => <option key={x}>{x}</option>)}</select></FilterField>
          <FilterField label="Total lots" title="Fixed lots mode: the box size. Allocate funds mode: calculated from the allocation">
            <input className={`${CONTROL} w-16 mono text-right disabled:opacity-50`} value={allocModeB ? (sized?.lots ?? "") : cfg.lots} disabled={allocModeB}
              onChange={(e) => setCfg({ ...cfg, lots: Math.max(1, Math.floor(Number(e.target.value) || 1)) })} data-testid="box-lots" /></FilterField>
          <FilterField label="Lots per order" title="The most lots in ONE broker order; each leg is sent in as many orders as needed. Capped by the account's limit (Admin -> Options access)">
            <input className={`${CONTROL} w-16 mono text-right`} value={lpo}
              onChange={(e) => { setLpo(Math.max(1, Math.floor(Number(e.target.value) || 1))); setSized(null); }} data-testid="box-lpo" /></FilterField>
          <FilterField label="Direction"><select className={CONTROL} value={cfg.direction} onChange={(e) => setCfg({ ...cfg, direction: e.target.value })}
            data-testid="box-direction"><option value="LONG">Long box</option><option value="SHORT">Short box</option></select></FilterField>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          {[0, 1].map((i) => (
            <FilterField key={i} label={i ? "Upper strike K2" : "Lower strike K1"}>
              <select className={`${CONTROL} w-28 mono`} value={cfg.strikes[i] ?? ""} data-testid={`box-k${i + 1}`}
                onChange={(e) => { const s = [...cfg.strikes]; s[i] = Number(e.target.value); setCfg({ ...cfg, strikes: s }); }}>
                {cfg.strikes[i] == null && <option value="">strike</option>}
                {strikes.map((k) => <option key={k} value={k}>{k}</option>)}</select>
            </FilterField>))}
          {validBox(cfg.strikes) && <span className="mono pb-2">width {k2 - k1}</span>}
          {!validBox(cfg.strikes) && cfg.strikes.every((k) => k != null) && <span className="text-[#FF3B30] pb-2">K1 must be below K2</span>}
        </div>
        {err && <div className="text-[#FF3B30]" data-testid="box-error">{err}</div>}
        <div className="space-y-1" data-testid="box-legs">
          {BOX_LEGS[cfg.direction].map(([side, kind, ki], i) => {
            const q = quote?.legs?.[i] || {};
            return (
              <div key={i} className="grid grid-cols-[2rem_3rem_2.4rem_1fr] gap-2 items-center py-1 border-t border-[var(--term-border)]" data-testid={`box-leg-${i + 1}`}>
                <span className="mono text-[var(--term-muted)]">L{i + 1}</span>
                <span className={`text-center px-1 rounded-sm text-[10px] font-bold ${sideCls(side)}`}>{side}</span>
                <span className="text-center px-1 rounded-sm text-[10px] font-bold bg-[#2563EB]/15 text-[#2563EB]">{kind}</span>
                <span className="mono text-[11px] flex flex-wrap gap-x-3">
                  <b>{q.tradingsymbol || `${cfg.strikes[ki] ?? "—"} ${kind}`}</b>
                  <span>bid {px(q.bid)}</span><span>ask {px(q.ask)}</span>
                  <span className="font-bold">{side === "SELL" ? "sell" : "buy"} @ {u2(q.price)}</span>
                  <span>age {age(q.quote_age_s)}</span>
                  {q.status && q.status !== "OK" && <span className="text-[#FF3B30] font-bold">{q.status}</span>}
                </span>
              </div>);
          })}
        </div>
        {quote && <div className="text-[10px] text-[var(--term-muted)] mono">
          spot {quote.spot ?? "—"} ({quote.underlying_symbol}) · qty {quote.quantity} × units {quote.units ?? "unverified"} · settlement {quote.settlement || "—"}
          · leg skew {u2(quote.quote_skew_s)}s {quote.stale ? <b className="text-[#b38f00]">· NOT A SIMULTANEOUS EXECUTABLE QUOTE</b> : ""}
          {quote.warnings?.length ? ` · ${quote.warnings.join(", ")}` : ""}</div>}
        {quote?.refusal && <div className="text-[#FF3B30]" data-testid="box-refusal">{quote.refusal.code}{quote.refusal.message ? `: ${quote.refusal.message}` : ""}</div>}
        {!trading && <div className="text-[10px] text-[var(--term-muted)]" data-testid="box-no-orders">Analysis only — no order can be placed from the Box Builder.</div>}
        {trading && <div className="flex flex-wrap items-end gap-2 pt-1" data-testid="box-shadow-create">
          <FilterField label="Min net P&L to start ₹" title="Start is refused if the live estimated net minimum expiry P&L is below this">
            <input className={`${CONTROL} w-24 mono text-right`} value={minNet} onChange={(e) => setMinNet(e.target.value)} data-testid="box-min-net" /></FilterField>
          <FilterField label="Sizing mode" title="Fixed lots: the Lots field above. Allocate funds: the largest whole number of box lots whose Zerodha basket margin + charges fits the allocation (2 % reserve), as Options Hedging sizes">
            <select className={CONTROL} value={sizingMode} onChange={(e) => { setSizingMode(e.target.value); setSized(null); }} data-testid="box-sizing-mode">
              <option value="FIXED_LOTS">Fixed lots</option><option value="ALLOCATE_FUNDS">Allocate funds</option></select></FilterField>
          {allocMode && <FilterField label="Allocate funds ₹">
            <input className={`${CONTROL} w-28 mono text-right`} value={alloc} onChange={(e) => { setAlloc(e.target.value); setSized(null); }}
              data-testid="box-alloc" /></FilterField>}
          {allocMode && <button type="button" disabled={busy || !quote?.metrics || !Number(alloc)} onClick={sizeIt} data-testid="box-size-calc"
            className="h-8 px-2 rounded-sm border border-[var(--term-border-strong)] text-xs disabled:opacity-40">Calculate lots</button>}
          {quote?.lot_size && <span className="text-[10px] mono pb-2" data-testid="box-slices">
            {resolvedLots ? sliceText(resolvedLots, quote.lot_size, quote.units, effLpo) : "lots not calculated yet"}</span>}
          {lpo > accountCap && <span className="text-[11px] text-[#FF3B30] w-full" data-testid="box-lpo-cap">
            {lpo} lots per order requested, but this account allows {accountCap} (options_max_lots_per_order). An admin can raise it for this
            account only: Admin → Options access → the account → Max lots per order. Create is disabled until it fits.</span>}
          <button type="button" disabled={busy || !quote?.metrics || quote?.stale || !shadow?.account_ready || (allocMode && !(sized?.lots > 0))
            || lpo > accountCap}
            onClick={() => createTrade("SHADOW")}
            data-testid="box-create-shadow" className="h-8 px-3 rounded-sm bg-[#7C3AED] text-white text-xs font-bold disabled:opacity-40">Create SHADOW trade</button>
          <LiveCreate live={live} underlying={cfg.underlying} testId="box-live" onConfirm={() => createTrade("LIVE")}
            disabled={busy || !quote?.metrics || quote?.stale || (allocMode && !(sized?.lots > 0)) || lpo > accountCap}
            lines={quote ? [`${quote.exchange}:${quote.underlying} ${quote.expiry} · ${cfg.direction} box K1 ${cfg.strikes[0]} / K2 ${cfg.strikes[1]}`,
              `${allocMode ? (sized?.lots || 0) : cfg.lots} lot(s) · ${lpo} lot(s) per order`,
              ...(quote.legs || []).map((l, i) => `L${i + 1} ${l.side} ${l.option_type} @ ${l.price}`),
              m ? `Net ${m.entry_kind === "CREDIT" ? "credit" : "debit"} ${m.net_entry_unit}/unit · est. net expiry P&L ${m.net_pnl != null ? rupees(m.net_pnl) : "—"}` : "",
              `Broker capital: ${cap?.capital_required != null ? rupees(cap.capital_required) : "fetch broker margin first"}`] : []} />
          {allocMode && sized && <div className="w-full text-[11px] mono flex flex-wrap gap-x-4" data-testid="box-sized">
            {sized.lots > 0 ? <>
              <span>Calculated lots <b>{sized.lots}</b></span><span>Qty per leg {sized.quantity}</span>
              <span>Basket margin {rupees(sized.basket?.capital_required)}</span><span>Capital incl. charges {rupees(sized.capital)}</span>
              <span>Reserve {rupees(sized.reserve)} ({sized.reserve_pct}%)</span><span>Unused allocation {rupees(sized.unused)}</span>
              <span className="text-[var(--term-muted)]">{sized.calls} broker calculator calls</span></>
              : <span className="text-[#FF3B30]">Not sizable: {(sized.reasons || []).join(", ")}</span>}
            {sized.lots > 0 && sized.reasons?.length ? <span className="text-[#b38f00]">{sized.reasons.join(", ")}</span> : null}
          </div>}
          <span className="text-[10px] text-[var(--term-muted)]">{shadow?.account_ready
            ? "creates a PLANNED trade; Start re-validates on live data · real orders to the unfunded SHADOW account (refused by Zerodha), simulated fills · LIVE not available"
            : `Box Spread SHADOW not ready: ${(shadow?.reasons || ["module off"]).join(", ")}`}</span>
        </div>}
      </div>
      <div className="space-y-2">
        {m && allocModeB && !(sized?.lots > 0) && <div className="text-[11px] font-bold text-[#b38f00] border border-[#b38f00] rounded-sm p-2"
          data-testid="box-indicative">INDICATIVE — 1-lot figures. Press "Calculate lots" to size the position from the allocation;
          nothing can be created until then.</div>}
        {m && resolvedLots && <div className="text-[11px] mono text-[var(--term-secondary)]" data-testid="box-position">
          Position analysed: {resolvedLots} lot{resolvedLots > 1 ? "s" : ""} × {quote.lot_size} = {quote.quantity} qty per leg
          {quote.orders_per_leg ? ` · ${quote.orders_per_leg} order(s) per leg in the charges` : ""}
          {allocModeB ? " · from the allocation" : " · fixed lots"}</div>}
        {m && <>
          <div className="flex flex-wrap gap-2" data-testid="box-metrics">
            <Tile label="Box width" value={m.width} sub="K2 − K1 (not a breakeven)" />
            <Tile label="Breakevens" value="none — flat payoff" sub="a box's expiry P&L is the same at every price" testId="box-breakevens" />
            <Tile label={m.entry_kind === "DEBIT" ? "Net debit" : "Net credit"} value={money(m.net_entry, m.net_entry_unit)} sub={`${u2(m.net_entry_unit)} / unit`} testId="box-net" />
            <Tile label={cfg.direction === "LONG" ? "Fixed expiry payout" : "Fixed expiry liability"} value={money(m.fixed_expiry_value, m.width)} />
            <Tile label="Min expiry P&L (gross)" value={money(m.min_pnl, m.min_pnl_unit)} tone={tone(m.min_pnl_unit)} sub={`${u2(m.min_pnl_unit)} / unit`} testId="box-min" />
            <Tile label="Max expiry P&L (gross)" value={money(m.max_pnl, m.max_pnl_unit)} tone={tone(m.max_pnl_unit)} testId="box-max" />
            <Tile label="Est. charges" value={m.costs != null ? rupees(m.costs) : "—"}
              sub={m.cost_breakdown ? `entry ${rupees(m.cost_breakdown.entry)} · settle ${m.cost_breakdown.settlement != null ? rupees(m.cost_breakdown.settlement) : "—"} · slip ${rupees(m.cost_breakdown.slippage)}` : "units unverified"} />
            <Tile label="Est. net min expiry P&L" value={m.net_pnl != null ? rupees(m.net_pnl) : "—"} tone={tone(m.net_pnl)} testId="box-netpnl"
              sub={m.net_pnl_lot != null ? `${rupees(m.net_pnl_lot)} / lot · ${u2(m.net_pnl_unit)} / unit` : ""} />
            <Tile label="Capital required" value={marginText(cap)} sub={cap?.span != null ? `margin ${rupees(cap.span + cap.exposure)}` : "broker basket calculator"} />
            <Tile label="Return on capital" value={pct(roc)} sub="net min P&L / capital required" />
          </div>
          <div className="flex gap-2 items-center">
            <button className="h-8 px-3 rounded-sm border border-[var(--term-border-strong)] text-xs disabled:opacity-40" disabled={busy}
              onClick={fetchMargin} data-testid="box-margin">Fetch broker margin</button>
            <span className="text-[10px] text-[var(--term-muted)]">Theoretical: assumes all four legs fill at these prices and are held to expiry. Not a guaranteed profit.
              {m.cost_breakdown?.basis ? ` Settlement estimate: ${m.cost_breakdown.basis}.` : ""}</span>
          </div>
          <div className="term-panel rounded-sm p-2">
            <IcPayoffChart payoff={quote.payoff} markers={markers}
              note={quote.model?.status === "OK" ? `model now ${money(quote.model.today_pnl, quote.model.today_pnl_unit)} (${quote.model.assumption})` : null} />
          </div>
          <table className="text-xs mono" data-testid="box-checks"><thead><tr className="text-[var(--term-muted)]">
            <th className="text-left pr-4">Underlying at expiry</th><th className="text-right">P&amp;L / unit (leg by leg)</th></tr></thead>
            <tbody>{(m.check_points || []).map((p) => <tr key={p.s}><td className="pr-4">{Number(p.s).toLocaleString("en-IN")}</td>
              <td className="text-right">{u2(p.pnl_unit)}</td></tr>)}</tbody></table>
        </>}
      </div>
    </div>
  );
}
